import { CompletedWalk } from "../types";
import { FONTS, GRAD, PINK, CREAM, CARD_BORDER, CARD_BG } from "../data/affirmations";

interface StatsTabProps {
  totalWalks: number;
  totalMiles: number;
  streak: number;
  completedWalks: CompletedWalk[];
}

export function StatsTab({ totalWalks, totalMiles, streak, completedWalks }: StatsTabProps) {
  const londonFact = totalMiles > 10
    ? "that's like walking from Camden to Brixton!"
    : totalMiles > 5
    ? "that's like walking from Hyde Park to Shoreditch!"
    : totalMiles > 2
    ? "keep going — you're slaying!"
    : "every mile is a slay 💕";

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <style>{FONTS + `
        @keyframes fadeUp { from { opacity:0;transform:translateY(16px) } to { opacity:1;transform:translateY(0) } }
      `}</style>

      <div style={{ marginBottom: "20px" }}>
        <div style={{ fontSize: "9px", letterSpacing: "5px", color: PINK, textTransform: "uppercase", fontWeight: 700, fontFamily: "'Poppins',sans-serif", marginBottom: "4px" }}>your london journey</div>
        <div style={{ fontFamily: "'Poppins',sans-serif", fontSize: "28px", fontWeight: 900, color: "#1a1a1a", letterSpacing: "-1px", lineHeight: 1 }}>Glow Up Stats</div>
      </div>

      {/* Total miles hero */}
      <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: "20px", padding: "28px", marginBottom: "12px", textAlign: "center" }}>
        <div style={{ fontSize: "9px", letterSpacing: "3px", color: PINK, textTransform: "uppercase", marginBottom: "6px", fontFamily: "'Poppins',sans-serif" }}>total miles across london</div>
        <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: "72px", background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1, marginBottom: "8px" }}>{totalMiles}</div>
        <div style={{ fontSize: "12px", color: "#999", fontFamily: "'Poppins',sans-serif" }}>{londonFact}</div>
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
        {[
          { label: "Walks Done", val: totalWalks, suffix: "" },
          { label: "Day Streak", val: streak, suffix: "🔥" },
          { label: "Drops Slayed", val: completedWalks.filter(w => w.title !== "Classic").length, suffix: "" },
          { label: "Zones Explored", val: new Set(completedWalks.map(w => w.neighbourhood.split(",")[1]?.trim() || "")).size || 0, suffix: "" },
        ].map(s => (
          <div key={s.label} style={{ background: "#fff", border: `1px solid ${CARD_BORDER}`, borderRadius: "16px", padding: "18px" }}>
            <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: "32px", background: GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1, marginBottom: "4px" }}>
              {s.val}{s.suffix}
            </div>
            <div style={{ fontSize: "9px", color: "#bbb", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Poppins',sans-serif" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Weekly bar chart */}
      <div style={{ background: "#fff", border: `1px solid ${CARD_BORDER}`, borderRadius: "18px", padding: "18px", marginBottom: "12px" }}>
        <div style={{ fontSize: "9px", color: "#bbb", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "14px", fontFamily: "'Poppins',sans-serif" }}>This Week</div>
        <div style={{ display: "flex", gap: "6px", alignItems: "flex-end", height: "70px" }}>
          {["M", "T", "W", "T", "F", "S", "S"].map((day, i) => {
            const heights = [60, 30, 80, 50, 70, 40, 0];
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                <div style={{ width: "100%", borderRadius: "6px 6px 0 0", background: heights[i] > 0 ? GRAD : "#F0ECE8", height: `${heights[i]}%`, transition: "height 0.5s ease" }} />
                <div style={{ fontSize: "9px", color: "#bbb", fontFamily: "'Poppins',sans-serif" }}>{day}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Completed walks */}
      <div style={{ background: "#fff", border: `1px solid ${CARD_BORDER}`, borderRadius: "18px", padding: "18px", marginBottom: "12px" }}>
        <div style={{ fontSize: "9px", color: "#bbb", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "14px", fontFamily: "'Poppins',sans-serif" }}>Completed Walks</div>
        {completedWalks.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>🚶‍♀️</div>
            <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: "15px", color: "#bbb" }}>No walks yet — go slay!</div>
          </div>
        ) : (
          completedWalks.map((w, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: i < completedWalks.length - 1 ? `1px solid ${CARD_BORDER}` : "none" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: GRAD, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: "18px" }}>{w.emoji}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: "14px", color: "#1a1a1a" }}>{w.title}</div>
                <div style={{ fontSize: "11px", color: "#999", fontFamily: "'Poppins',sans-serif" }}>{w.neighbourhood} · {w.distance}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: PINK, fontFamily: "'Poppins',sans-serif" }}>{w.time}</div>
                <div style={{ fontSize: "10px", color: "#bbb", fontFamily: "'Poppins',sans-serif" }}>{w.date}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Favourite drop */}
      <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: "18px", padding: "20px", textAlign: "center" }}>
        <div style={{ fontSize: "9px", color: PINK, letterSpacing: "3px", textTransform: "uppercase", fontWeight: 700, marginBottom: "8px", fontFamily: "'Poppins',sans-serif" }}>Favourite Drop</div>
        <div style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: "20px", color: "#1a1a1a", marginBottom: "4px" }}>
          {completedWalks.length > 0 ? completedWalks[0].title : "The Amy Walk"}
        </div>
        <div style={{ fontSize: "12px", color: "#999", fontFamily: "'Poppins',sans-serif" }}>
          {completedWalks.length > 0 ? completedWalks[0].neighbourhood : "Camden · do your first walk!"}
        </div>
      </div>
    </div>
  );
}
